import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { type SendReminderVariables, SendReminderVariablesSchema } from './send-reminder-variables';

type SendFormalNoticeVariables = SendReminderVariables;

async function sendFormalNoticeFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: SendFormalNoticeVariables,
): Promise<ApiResponse> {
  const {
    invoiceId,
    additionalInvoiceIds,
    creditNoteIds,
    accountingPaymentIds,
    tpPaymentIds,
    sendingOptions,
  } = SendReminderVariablesSchema.parse(variables);
  const instance = await axiosInstance;

  return instance.post(`invoices/${invoiceId}/formal_notice`, {
    ...sendingOptions,
    additionalInvoices: additionalInvoiceIds,
    creditNotes: creditNoteIds,
    accountingPayments: accountingPaymentIds,
    tpPayments: tpPaymentIds,
  });
}

export function useSendFormalNotice() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, SendFormalNoticeVariables>({
    mutationFn: variables => sendFormalNoticeFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['unhandledClient'] });
      client.invalidateQueries({ queryKey: ['unhandledClients'] });
      client.invalidateQueries({ queryKey: ['unhandled-debtors-view'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    SendFormalNoticeVariables,
    'sendFormalNotice'
  >('sendFormalNotice', mutation);
}
